import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';
import type { MarkdownConfig } from '@lezer/markdown';

/** Obsidian ==highlight== inline syntax as a Lezer markdown extension
 * (same delimiter mechanism as GFM strikethrough). */
const HighlightDelim = { resolve: 'Highlight', mark: 'HighlightMark' };

export const HighlightExtension: MarkdownConfig = {
  defineNodes: [{ name: 'Highlight' }, { name: 'HighlightMark' }],
  parseInline: [
    {
      name: 'Highlight',
      parse(cx, next, pos) {
        if (next !== 61 /* '=' */ || cx.char(pos + 1) !== 61) return -1;
        return cx.addDelimiter(HighlightDelim, pos, pos + 2, true, true);
      },
      after: 'Emphasis',
    },
  ],
};

/** GFM markdown (tables, task lists, strikethrough, autolinks) + highlight,
 * with fenced-code language highlighting. Includes the markdown keymap
 * (Enter continues lists, Backspace deletes markup). */
export function markdownLang() {
  return markdown({
    base: markdownLanguage,
    codeLanguages: languages,
    extensions: [HighlightExtension],
  });
}

/** Theme-variable-driven syntax colors (used by source mode and by the
 * revealed syntax around the cursor in live mode). */
const mdHighlight = HighlightStyle.define([
  { tag: t.heading, color: 'var(--text-normal)', fontWeight: '650' },
  { tag: t.strong, fontWeight: '700' },
  { tag: t.emphasis, fontStyle: 'italic' },
  { tag: t.strikethrough, textDecoration: 'line-through' },
  { tag: t.monospace, fontFamily: 'var(--font-mono)', fontSize: '0.9em' },
  { tag: t.processingInstruction, color: 'var(--text-faint)' },
  { tag: t.punctuation, color: 'var(--text-faint)' },
  { tag: t.url, color: 'var(--text-faint)' },
  { tag: t.link, color: 'var(--link)' },
  { tag: t.quote, color: 'var(--text-muted)' },
  { tag: t.contentSeparator, color: 'var(--text-faint)' },
  { tag: t.meta, color: 'var(--text-muted)' },
  // fenced code tokens
  { tag: t.keyword, color: 'var(--accent-strong)' },
  { tag: [t.string, t.special(t.string)], color: 'var(--success)' },
  { tag: t.comment, color: 'var(--text-faint)', fontStyle: 'italic' },
  { tag: [t.number, t.bool, t.atom], color: 'var(--warning)' },
  { tag: [t.function(t.variableName), t.function(t.propertyName)], color: 'var(--link)' },
  { tag: [t.typeName, t.className], color: 'var(--warning)' },
  { tag: t.operator, color: 'var(--text-muted)' },
]);

export const mdSyntaxHighlighting = syntaxHighlighting(mdHighlight, { fallback: true });
